import { useCallback, useContext, useMemo, useState } from "react";
import { getWeb3ProviderForChain } from "../utils/wallet/activeSigner";
import { AuthContext } from "../contexts/AuthContext";
import { getCurrentChainConfig } from "../config/chains";
import {
  enableOneClick as enableOneClickUtil,
  disableOneClick as disableOneClickUtil,
  revokePendingAgent as revokePendingAgentUtil,
  getOneClickState,
  getPendingRevokes,
  isOneClickActive,
} from "../utils/oneClick";
import { getPerpDomainName, getSpotDomainName } from "../utils/gaslessDomains";

/**
 * One-click (session-key) trading control. Exposes whether one-click is supported on the current
 * chain, whether it's currently active, and enable/disable actions. Enabling pops the wallet ONCE
 * (to sign the agent approval); thereafter orders sign silently with the agent key.
 */
export function useOneClickTrading() {
  const { account: rawAccount, masterAccount } = useContext(AuthContext);
  // One-click is always about the MASTER's agent, even while viewing/trading a subaccount.
  const account = masterAccount || rawAccount;
  const config = getCurrentChainConfig();
  const chainId = config.chainId;
  const perpEngine =
    config.contracts.perpetualDex?.perpRouter || config.contracts.perpetualDex?.orderHandler;
  const spotEngine = config.contracts.perpetualDex?.router;
  const agentRegistry = config.contracts.perpetualDex?.agentRegistry;
  const perpDomainName = getPerpDomainName(config);
  const spotDomainName = getSpotDomainName(config);
  const relayerUrl = config.relayerUrl;
  // One-click is supported as long as the relayer + at least one engine (perp OR spot) exist.
  // Spot-only chains (no perpRouter/orderHandler) still get gasless spot orders via the agent key.
  const supported = !!(config.gasless && relayerUrl && (perpEngine || spotEngine));

  const [version, setVersion] = useState(0); // bump to re-read localStorage
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useMemo(
    () => (account ? getOneClickState(account, chainId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, chainId, version]
  );
  const active = useMemo(
    () => (account ? isOneClickActive(account, chainId) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, chainId, version]
  );

  const enable = useCallback(
    async (durationDays?: number) => {
      if (!account) throw new Error("Connect your wallet first");
      if (!supported) throw new Error("One-click trading isn't available on this network");
      setError(null);
      setIsEnabling(true);
      try {
        const provider = await getWeb3ProviderForChain(config);
        await enableOneClickUtil({
          provider,
          account,
          chainId,
          perpEngine: perpEngine as string,
          spotEngine: spotEngine as string | undefined,
          agentRegistry: agentRegistry as string | undefined,
          perpDomainName,
          spotDomainName,
          relayerUrl: relayerUrl as string,
          durationDays,
        });
        setVersion((v) => v + 1);
      } catch (e: any) {
        setError(e?.message || "Failed to enable one-click trading");
        throw e;
      } finally {
        setIsEnabling(false);
      }
    },
    [account, chainId, perpEngine, spotEngine, agentRegistry, perpDomainName, spotDomainName, relayerUrl, supported]
  );

  /**
   * Turn one-click off. `revokeOnChain` also revokes the approval on-chain (a
   * gas tx); a failure is reported in `error` and rethrown, never swallowed,
   * and the key is kept disabled so the revoke can be retried.
   */
  const disable = useCallback(
    async (revokeOnChain = false) => {
      if (!account) return;
      setError(null);
      setIsDisabling(true);
      try {
        await disableOneClickUtil(account, chainId, {
          revokeOnChain,
          provider: revokeOnChain ? () => getWeb3ProviderForChain(config) : undefined,
          perpEngine,
          spotEngine,
          agentRegistry,
        });
      } catch (e: any) {
        setError(e?.message || "Failed to revoke one-click trading on-chain");
        throw e;
      } finally {
        setIsDisabling(false);
        setVersion((v) => v + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, chainId, perpEngine, spotEngine, agentRegistry]
  );

  // Off here, but an approval this browser could still revoke is live on-chain
  // (a revoke failed, so the key was kept disabled for a retry).
  const revokePending =
    !!state && !state.enabled && state.expiry > Math.floor(Date.now() / 1000);

  /**
   * Every agent whose revoke failed and whose approval has not yet lapsed.
   *
   * Survives re-enabling, which replaces `state` with a new agent. Before this,
   * that overwrite discarded the old agent's address and the retry button
   * vanished with it, leaving the approval live for the rest of its 30 days with
   * nothing in the UI able to name it.
   */
  const pendingRevokes = useMemo(
    () => getPendingRevokes(account, chainId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, chainId, version]
  );

  /** Retry one stranded agent BY ADDRESS; `state` may since be a different one. */
  const revokeStrandedAgent = useCallback(
    async (agentAddress: string) => {
      if (!account) return;
      setError(null);
      setIsDisabling(true);
      try {
        await revokePendingAgentUtil(account, chainId, agentAddress, {
          provider: () => getWeb3ProviderForChain(config),
          perpEngine,
          spotEngine,
          agentRegistry,
        });
      } catch (e: any) {
        setError(e?.message || "Failed to revoke the old approval on-chain");
        throw e;
      } finally {
        setIsDisabling(false);
        setVersion((v) => v + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, chainId, perpEngine, spotEngine, agentRegistry]
  );

  return {
    supported,
    active,
    state,
    enable,
    disable,
    isEnabling,
    isDisabling,
    revokePending,
    pendingRevokes,
    revokeStrandedAgent,
    error,
  };
}
